/**
 * Who made a version of an asset's art (issue #215): one of our freelancers, or
 * someone outside by name with an optional link.
 */

/**
 * An author as the API answers it.
 * @typedef {object} Author
 * @property {string | null} user_id the freelancer's user id, or null for an outside author
 * @property {string} name the freelancer's name, or the outside author's
 * @property {string | null} url an outside author's link
 */

/**
 * A freelancer as /api/freelancers answers one.
 * @typedef {object} Freelancer
 * @property {string} id
 * @property {string} [first_name]
 * @property {string} [last_name]
 * @property {string} [username]
 * @property {string} [email]
 * @property {string} [role]
 */

/**
 * What an author editor's form holds.
 * @typedef {object} AuthorDraft
 * @property {'freelancer' | 'outside'} kind
 * @property {string} userId
 * @property {string} name
 * @property {string} url
 */

/**
 * The author's name as a credit reads, or '' when unknown.
 * @param {Author | null | undefined} author
 * @returns {string}
 */
export function authorLabel(author) {
    return author?.name || '';
}

/**
 * A freelancer as a picker lists them: their real name, else their username or
 * email, marked when they're staff.
 * @param {Freelancer} f
 * @returns {string}
 */
export function freelancerLabel(f) {
    const fullName = [f.first_name, f.last_name].filter(Boolean).join(' ');
    const base = fullName || f.username || f.email || '';
    return f.role === 'staff' ? `${base} (staff)` : base;
}

/**
 * The form an editor opens with for an author.
 * @param {Author | null | undefined} author
 * @returns {AuthorDraft}
 */
export function authorDraft(author) {
    if (author?.user_id) return { kind: 'freelancer', userId: author.user_id, name: '', url: '' };
    return {
        kind: author ? 'outside' : 'freelancer',
        userId: '',
        name: author?.name || '',
        url: author?.url || '',
    };
}

/**
 * The PUT body that records a draft, or an error to show instead.
 * @param {AuthorDraft} draft
 * @returns {{ body: { user_id?: string, name?: string, url?: string } } | { error: string }}
 */
export function authorPayload(draft) {
    if (draft.kind === 'freelancer') {
        if (!draft.userId) return { error: 'Choose a freelancer.' };
        return { body: { user_id: draft.userId } };
    }
    const name = draft.name.trim();
    const url = draft.url.trim();
    if (!name) return { error: "Enter the author's name." };
    if (url && !/^https?:\/\/[^\s/]+/i.test(url))
        return { error: 'The link must start with http:// or https://.' };
    return { body: url ? { name, url } : { name } };
}
